import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { queryKeys } from '@/lib/query-keys';
import type { CustomFieldDefinition } from '@/lib/filters/types';

const STALE_TIME = 5 * 60 * 1000; // 5 minutes
const GC_TIME = 10 * 60 * 1000; // 10 minutes

async function fetchCustomFields(workspaceId: string): Promise<CustomFieldDefinition[]> {
  const { data, error } = await supabase
    .from('custom_fields')
    .select('id, label, data_type, workspace_id, created_at')
    .eq('workspace_id', workspaceId)
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Hook to fetch custom field definitions for a workspace.
 * Used by ContactFilters to populate the field dropdown with custom fields.
 */
export function useCustomFields(workspaceId?: string | null) {
  const {
    data: customFields = [],
    isPending: loading,
    error,
    refetch,
  } = useQuery({
    queryKey: queryKeys.customFields.list(workspaceId ?? undefined),
    queryFn: () => fetchCustomFields(workspaceId!),
    enabled: !!workspaceId,
    staleTime: STALE_TIME,
    gcTime: GC_TIME,
    throwOnError: false,
  });

  return {
    customFields,
    loading,
    error,
    refetch,
  };
}
